import { ChangeDetectionStrategy, Component, HostListener, computed, input, output, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { DialogComponent } from '@app/components/dialog/dialog.component';
import { ExternalSceneActionComponent } from '../external-scene-action/external-scene-action.component';
import { ExternalSceneRowViewModel } from '../../external-scenes/external-scenes.models';
import { DOWNLOAD_SPEED_MBIT_PER_SECOND } from '../../external-scenes/external-scenes-format.util';
import { formatCoordinates } from '../../external-scenes/external-scenes-map.util';
import { resolveProviderClass } from '../../external-scenes/external-scenes-view.util';

/**
 * Large Quick View of one external scene with its metadata and the same Load action as the table row.
 * The parent passes the live row from the store, so the status and the button follow a Load while it is open.
 */
@Component({
  selector: 'app-external-scene-quick-view-dialog',
  standalone: true,
  templateUrl: './external-scene-quick-view-dialog.component.html',
  styleUrl: './external-scene-quick-view-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DatePipe, TranslatePipe, DialogComponent, ExternalSceneActionComponent]
})
export class ExternalSceneQuickViewDialogComponent {
  readonly row = input<ExternalSceneRowViewModel | null>(null);
  readonly visible = input(false);

  readonly closed = output<void>();
  /** Emits the row id when the dialog's Load button is pressed. */
  readonly loadClick = output<string>();

  readonly downloadSpeed = DOWNLOAD_SPEED_MBIT_PER_SECOND;

  /** Preview URL that failed to load; a different row (or URL) gets a fresh attempt. */
  private readonly failedPreviewUrl = signal<string | null>(null);

  readonly showPreview = computed(() => {
    const url = this.row()?.previewUrl ?? null;
    return url !== null && url !== this.failedPreviewUrl();
  });

  readonly providerClass = computed(() => {
    const row = this.row();
    return row ? resolveProviderClass(row.provider) : '';
  });

  readonly coordinatesLabel = computed(() => {
    const row = this.row();
    return row ? formatCoordinates(row.centerLatitude, row.centerLongitude) : '';
  });

  onPreviewError(): void {
    this.failedPreviewUrl.set(this.row()?.previewUrl ?? null);
  }

  /**
   * Closes the open dialog on Escape. `app-dialog` registers its own Escape listener only in `show()`, which a
   * dialog driven by the `visible` input never calls, so the key is handled here.
   */
  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.visible() && this.row()) {
      this.closed.emit();
    }
  }

  onVisibleChange(visible: boolean): void {
    if (!visible) {
      this.closed.emit();
    }
  }
}
